/**
 * Portal multi-account "pay across my accounts" view (PD-14190). Shown on the
 * Make Payment page when the portal user manages more than one account. Open
 * invoices are grouped by account; each account section has its own payment-
 * method selector that DEFAULTS to the logged-in account's method (picked at
 * the bottom) but can be OVERRIDDEN to that account's own saved method or its
 * own Account Funds. One submit fans out to one payment per account and the
 * result is reported per account.
 */

import { faPlus, faWallet } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PaymentMethodTypeEnum } from '@supply-mover-2-0/core';
import type {
	PortalCheckoutPaymentMethodDto,
	PortalPayAccountAllocationDto,
	PortalPayAccountResultDto,
	PortalPayAccountsContextDto,
} from '@supply-mover-2-0/shared';
import {
	DateRangePicker,
	Sidebar,
	Tooltip,
	useTranslation,
} from '@supply-mover-2-0/web-ui';
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { parseIsoDate, toIsoDate } from '../lib/dateRange';
import { formatCurrency } from '../lib/formatCurrency';
import * as portalPaymentService from '../services/portalPaymentService';
import { useLoginModalStore } from '../stores/loginModalStore';
import { PortalAddPaymentMethod } from './PortalAddPaymentMethod';

const ACCOUNT_FUNDS = 'Account Funds';
const DEFAULT_CHOICE = 'default';
const FUNDS_CHOICE = 'funds';

interface LineStateModel {
	checked: boolean;
	amount: string;
}

function round2(value: number): number {
	return Math.round(value * 100) / 100;
}

function parseAmount(raw: string): number {
	const parsed = parseFloat(raw);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function methodLabel(m: PortalCheckoutPaymentMethodDto): string {
	const last = m.lastFour != null ? `****${m.lastFour}` : '';
	const name = m.name != null ? `(${m.name})` : '';
	return `${m.subtype ?? m.type} ${last} ${name}`.replace(/\s+/g, ' ').trim();
}

export const PayAccountsView: React.FC = () => {
	const { t } = useTranslation();
	const { token, isAuthenticated } = usePortalAuth();
	const navigate = useNavigate();
	const openLoginModal = useLoginModalStore((s) => s.open);

	useEffect(() => {
		if (!isAuthenticated) {
			openLoginModal();
		}
	}, [isAuthenticated, openLoginModal]);

	const [context, setContext] = useState<PortalPayAccountsContextDto | null>(
		null,
	);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState<string | null>(null);

	const [lines, setLines] = useState<Record<number, LineStateModel>>({});
	/** Per-account method choice keyed by clientId: 'default' | 'funds' | a
	 *  saved-method id (as a string). */
	const [accountChoice, setAccountChoice] = useState<Record<number, string>>(
		{},
	);
	/** Per-account on-account deposit amount (raw user input) keyed by clientId
	 *  (PD-14286). Money placed on that account's balance, not on an invoice. */
	const [onAccountByClient, setOnAccountByClient] = useState<
		Record<number, string>
	>({});
	const [defaultMethodId, setDefaultMethodId] = useState<number | null>(null);

	const [dateFrom, setDateFrom] = useState('');
	const [dateTo, setDateTo] = useState('');
	const [overdueOnly, setOverdueOnly] = useState(false);
	// PD-14285 — narrow the (potentially long) multi-account list to a single
	// location; null = "All locations" (the default, view-all behaviour).
	const [locationFilter, setLocationFilter] = useState<number | null>(null);
	const selectAllRef = useRef<HTMLInputElement>(null);

	const [showAddPayment, setShowAddPayment] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [results, setResults] =
		useState<Array<PortalPayAccountResultDto> | null>(null);

	const fetchContext = useCallback(async (): Promise<void> => {
		if (token == null) {
			return;
		}
		setLoading(true);
		setLoadError(null);
		try {
			const res = await portalPaymentService.getPayAccountsContext(token);
			if (res.success) {
				setContext(res.data);
				const initLines: Record<number, LineStateModel> = {};
				const initChoice: Record<number, string> = {};
				for (const acct of res.data.accounts) {
					initChoice[acct.clientId] = DEFAULT_CHOICE;
					for (const inv of acct.openInvoices) {
						initLines[inv.id] = { checked: false, amount: '' };
					}
				}
				setLines(initLines);
				setAccountChoice(initChoice);
				setOnAccountByClient({});
				const methods = res.data.defaultSavedPaymentMethods;
				const primary = methods.find((m) => m.isPrimary);
				setDefaultMethodId(
					primary != null
						? primary.id
						: methods.length > 0
							? methods[0].id
							: null,
				);
			}
		} catch (err) {
			setLoadError(
				err instanceof Error ? err.message : t('Failed to load payment data'),
			);
		} finally {
			setLoading(false);
		}
	}, [token, t]);

	useEffect(() => {
		void fetchContext();
	}, [fetchContext]);

	const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);

	// Accounts with their invoices narrowed by the Date/Overdue filters;
	// accounts left with no matching invoices are dropped from the view.
	const filteredAccounts = useMemo(
		() =>
			(context?.accounts ?? [])
				.filter(
					(acct) => locationFilter == null || acct.clientId === locationFilter,
				)
				.map((acct) => ({
					...acct,
					openInvoices: acct.openInvoices.filter((inv) => {
						const d = inv.date?.slice(0, 10) ?? null;
						if (dateFrom !== '' && (d == null || d < dateFrom)) {
							return false;
						}
						if (dateTo !== '' && (d == null || d > dateTo)) {
							return false;
						}
						if (overdueOnly) {
							const due = inv.dueDate?.slice(0, 10) ?? null;
							if (due == null || due >= todayIso) {
								return false;
							}
						}
						return true;
					}),
				}))
				// Keep an account when it has invoices to pay, OR when account
				// funds are enabled (so an on-account deposit is possible even
				// with no open invoices) — PD-14286.
				.filter(
					(acct) => acct.openInvoices.length > 0 || acct.accountFunds.enabled,
				),
		[
			context?.accounts,
			dateFrom,
			dateTo,
			overdueOnly,
			todayIso,
			locationFilter,
		],
	);

	const filtersActive =
		dateFrom !== '' || dateTo !== '' || overdueOnly || locationFilter != null;

	const outstandingById = useMemo(() => {
		const map = new Map<number, number>();
		for (const acct of context?.accounts ?? []) {
			for (const inv of acct.openInvoices) {
				map.set(inv.id, inv.outstandingBalance);
			}
		}
		return map;
	}, [context?.accounts]);

	const checkedIds = useMemo(() => {
		const ids = new Set<number>();
		for (const [id, line] of Object.entries(lines)) {
			if (line.checked) {
				ids.add(Number(id));
			}
		}
		return ids;
	}, [lines]);

	const visibleIds = useMemo(
		() => filteredAccounts.flatMap((a) => a.openInvoices.map((i) => i.id)),
		[filteredAccounts],
	);
	const allChecked =
		visibleIds.length > 0 && visibleIds.every((id) => checkedIds.has(id));
	const someChecked = visibleIds.some((id) => checkedIds.has(id));

	useEffect(() => {
		if (selectAllRef.current != null) {
			selectAllRef.current.indeterminate = someChecked && !allChecked;
		}
	}, [someChecked, allChecked]);

	// Account funds is a tenant-level capability — every account block carries
	// the same `enabled` flag, so any one of them tells us whether on-account
	// deposits are possible at all (PD-14286).
	const accountFundsEnabled = (context?.accounts ?? []).some(
		(a) => a.accountFunds.enabled,
	);

	// The on-account amount that will actually be charged for an account: zero
	// when it's paying with Account Funds (you can't deposit funds back onto the
	// account), otherwise the raw typed amount.
	const onAccountFor = (clientId: number): number => {
		if ((accountChoice[clientId] ?? DEFAULT_CHOICE) === FUNDS_CHOICE) {
			return 0;
		}
		return parseAmount(onAccountByClient[clientId] ?? '');
	};

	// Grand total = invoice amounts + on-account deposits, summed over the
	// currently-visible (filtered) accounts so the figure matches exactly what
	// the submit will charge.
	const total = useMemo(
		() =>
			round2(
				filteredAccounts.reduce((sum, acct) => {
					const invoiceSum = acct.openInvoices.reduce(
						(s, inv) =>
							s +
							(lines[inv.id].checked ? parseAmount(lines[inv.id].amount) : 0),
						0,
					);
					const choice = accountChoice[acct.clientId] ?? DEFAULT_CHOICE;
					const deposit =
						choice === FUNDS_CHOICE
							? 0
							: parseAmount(onAccountByClient[acct.clientId] ?? '');
					return sum + invoiceSum + deposit;
				}, 0),
			),
		[filteredAccounts, lines, accountChoice, onAccountByClient],
	);

	const lineErrors = useMemo(() => {
		const errors = new Map<number, string>();
		for (const [idStr, line] of Object.entries(lines)) {
			if (!line.checked) {
				continue;
			}
			const outstanding = outstandingById.get(Number(idStr));
			if (
				outstanding != null &&
				parseAmount(line.amount) > outstanding + 0.005
			) {
				errors.set(Number(idStr), t('Exceeds outstanding balance'));
			}
		}
		return errors;
	}, [lines, outstandingById, t]);

	// Does any account that is contributing (a checked invoice OR an on-account
	// deposit) rely on the default method? If so a default method is required.
	const anyUsesDefault = useMemo(
		() =>
			filteredAccounts.some((acct) => {
				if (
					(accountChoice[acct.clientId] ?? DEFAULT_CHOICE) !== DEFAULT_CHOICE
				) {
					return false;
				}
				const hasInvoice = acct.openInvoices.some(
					(inv) => lines[inv.id].checked,
				);
				const hasDeposit =
					parseAmount(onAccountByClient[acct.clientId] ?? '') > 0;
				return hasInvoice || hasDeposit;
			}),
		[filteredAccounts, accountChoice, lines, onAccountByClient],
	);

	const canSubmit =
		!submitting &&
		total > 0 &&
		lineErrors.size === 0 &&
		(!anyUsesDefault || defaultMethodId != null);

	// ─── Handlers ────────────────────────────────────────────────────
	const clearResults = (): void => {
		setResults(null);
		setSubmitError(null);
	};

	const toggleLine = (invoiceId: number, outstanding: number): void => {
		setLines((prev) => {
			const current = prev[invoiceId] ?? { checked: false, amount: '' };
			const checked = !current.checked;
			return {
				...prev,
				[invoiceId]: { checked, amount: checked ? outstanding.toFixed(2) : '' },
			};
		});
		clearResults();
	};

	const setLineAmount = (invoiceId: number, amount: string): void => {
		setLines((prev) => ({ ...prev, [invoiceId]: { checked: true, amount } }));
		clearResults();
	};

	const blurCurrency = (raw: string): string => {
		const parsed = parseFloat(raw);
		return Number.isFinite(parsed) && parsed > 0 ? parsed.toFixed(2) : '';
	};

	const toggleSelectAll = (): void => {
		const shouldCheck = !allChecked;
		setLines((prev) => {
			const next = { ...prev };
			for (const id of visibleIds) {
				next[id] = shouldCheck
					? { checked: true, amount: (outstandingById.get(id) ?? 0).toFixed(2) }
					: { checked: false, amount: '' };
			}
			return next;
		});
		clearResults();
	};

	// Per-account (per ownership/parent collection) select-all: toggles only
	// that account's currently-visible invoices, independent of the global one.
	const toggleAccountSelectAll = (invoices: Array<{ id: number }>): void => {
		const ids = invoices.map((inv) => inv.id);
		const allOn = ids.length > 0 && ids.every((id) => checkedIds.has(id));
		setLines((prev) => {
			const next = { ...prev };
			for (const id of ids) {
				next[id] = allOn
					? { checked: false, amount: '' }
					: {
							checked: true,
							amount: (outstandingById.get(id) ?? 0).toFixed(2),
						};
			}
			return next;
		});
		clearResults();
	};

	const accountName = (clientId: number): string =>
		(context?.accounts ?? []).find((a) => a.clientId === clientId)
			?.clientName ?? `#${clientId}`;

	const handleSubmit = async (): Promise<void> => {
		if (token == null || !canSubmit || context == null) {
			return;
		}
		setSubmitting(true);
		setSubmitError(null);
		setResults(null);
		try {
			const accounts = filteredAccounts
				.map((acct): PortalPayAccountAllocationDto | null => {
					const invoiceAllocations = acct.openInvoices
						.filter(
							(inv) =>
								lines[inv.id].checked && parseAmount(lines[inv.id].amount) > 0,
						)
						.map((inv) => ({
							invoiceId: inv.id,
							amount: parseAmount(lines[inv.id].amount),
						}));
					const choice = accountChoice[acct.clientId] ?? DEFAULT_CHOICE;
					// Account Funds can't be deposited back onto the account, so an
					// on-account amount is only valid when NOT paying with funds.
					const onAccountAmount =
						choice === FUNDS_CHOICE
							? 0
							: parseAmount(onAccountByClient[acct.clientId] ?? '');
					// Skip accounts that aren't contributing anything (no checked
					// invoices and no deposit) — PD-14286.
					if (invoiceAllocations.length === 0 && onAccountAmount <= 0) {
						return null;
					}
					const group: PortalPayAccountAllocationDto = {
						clientId: acct.clientId,
						invoiceAllocations,
					};
					if (onAccountAmount > 0) {
						group.onAccountAmount = onAccountAmount;
					}
					if (choice === FUNDS_CHOICE) {
						group.overridePaymentMethodType = ACCOUNT_FUNDS;
					} else if (choice !== DEFAULT_CHOICE) {
						group.overridePaymentMethodId = parseInt(choice, 10);
					}
					return group;
				})
				.filter((g): g is PortalPayAccountAllocationDto => g != null);

			const res = await portalPaymentService.payAccounts(token, {
				defaultPaymentMethodId: defaultMethodId ?? undefined,
				accounts,
			});
			setResults(res.results);
			if (res.success) {
				window.setTimeout(() => {
					void navigate('/invoices');
				}, 2500);
			}
		} catch (err) {
			setSubmitError(
				err instanceof Error
					? err.message
					: t('Payment failed. Please try again.'),
			);
		} finally {
			setSubmitting(false);
		}
	};

	// ─── Render ──────────────────────────────────────────────────────
	if (!isAuthenticated) {
		return <div />;
	}

	if (loading) {
		return (
			<div className="flex items-center justify-center py-16">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-portal-accent" />
			</div>
		);
	}

	if (loadError != null) {
		return (
			<div className="p-4">
				<div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
					<p className="text-sm text-red-600 dark:text-red-400">
						{t(loadError)}
					</p>
				</div>
			</div>
		);
	}

	const hasAnyAccounts = (context?.accounts.length ?? 0) > 0;

	return (
		<div className="px-4 py-4 max-w-3xl mx-auto">
			<h1 className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">
				{t('Make a Payment')}
			</h1>
			<p className="text-sm text-brand-muted mb-4">
				{accountFundsEnabled
					? t('Pay open invoices or add funds to your accounts in one place.')
					: t('Pay open invoices across your accounts in one place.')}
			</p>

			{!hasAnyAccounts ? (
				<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
					<p className="text-sm text-brand-muted">
						{t('No open invoices on any of your accounts.')}
					</p>
				</div>
			) : (
				<>
					{/* Filters + bulk select */}
					<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
						{/*
						  Single-row filter header: an unlabeled global select-all
						  (across every account card), the Date range, Overdue only,
						  and Clear filters, all bottom-aligned (feedback 2026-06-17).
						*/}
						<div className="flex flex-wrap items-end gap-3">
							<Tooltip content={t('Select all')} position="top">
								<label className="flex items-center min-h-touch cursor-pointer">
									<input
										ref={selectAllRef}
										type="checkbox"
										aria-label={t('Select all')}
										checked={allChecked}
										disabled={visibleIds.length === 0}
										onChange={toggleSelectAll}
										className="w-4 h-4 text-portal-accent disabled:opacity-50"
									/>
								</label>
							</Tooltip>
							{/*
							  Location filter (PD-14285) — only worth showing when the
							  user actually has more than one account with open invoices;
							  options are the full account list (not the date/overdue-
							  narrowed view) so a location is always selectable.
							*/}
							{(context?.accounts.length ?? 0) > 1 && (
								<div className="min-w-[12rem]">
									<label className="block text-xs font-medium text-brand-muted mb-1">
										{t('Location')}
									</label>
									<select
										value={locationFilter == null ? '' : String(locationFilter)}
										onChange={(e) => {
											const value = e.target.value;
											setLocationFilter(
												value === '' ? null : parseInt(value, 10),
											);
										}}
										aria-label={t('Filter by location')}
										className="w-full px-3 py-2 rounded-lg bg-brand-input border border-brand-border text-sm text-gray-900 dark:text-gray-100"
									>
										<option value="">{t('All locations')}</option>
										{(context?.accounts ?? []).map((acct) => (
											<option key={acct.clientId} value={String(acct.clientId)}>
												{t(acct.clientName ?? `#${acct.clientId}`)}
											</option>
										))}
									</select>
								</div>
							)}
							<div className="min-w-[15rem]">
								<label className="block text-xs font-medium text-brand-muted mb-1">
									{t('Date')}
								</label>
								<DateRangePicker
									startDate={parseIsoDate(dateFrom)}
									endDate={parseIsoDate(dateTo)}
									onChange={(dates: [Date | null, Date | null]) => {
										setDateFrom(toIsoDate(dates[0]));
										setDateTo(toIsoDate(dates[1]));
									}}
									isClearable={false}
									placeholderText={t('Select date range...')}
								/>
							</div>
							<label className="flex items-center gap-2 cursor-pointer min-h-touch">
								<input
									type="checkbox"
									checked={overdueOnly}
									onChange={(e) => {
										setOverdueOnly(e.target.checked);
									}}
									className="w-4 h-4 text-portal-accent"
								/>
								<span className="text-sm text-gray-900 dark:text-gray-100">
									{t('Overdue only')}
								</span>
							</label>
							{filtersActive && (
								<button
									type="button"
									onClick={() => {
										setDateFrom('');
										setDateTo('');
										setOverdueOnly(false);
										setLocationFilter(null);
									}}
									className="text-xs font-medium text-portal-accent hover:underline min-h-touch"
								>
									{t('Clear filters')}
								</button>
							)}
						</div>
					</div>

					{filteredAccounts.length === 0 ? (
						<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
							<p className="text-sm text-brand-muted">
								{t('No invoices match your filters.')}
							</p>
						</div>
					) : (
						filteredAccounts.map((acct) => {
							const choice = accountChoice[acct.clientId] ?? DEFAULT_CHOICE;
							const acctIds = acct.openInvoices.map((inv) => inv.id);
							const acctAllChecked =
								acctIds.length > 0 && acctIds.every((id) => checkedIds.has(id));
							const acctSomeChecked = acctIds.some((id) => checkedIds.has(id));
							// What this account is charged: checked invoice amounts +
							// its on-account deposit (PD-14286).
							const acctInvoiceSum = round2(
								acct.openInvoices.reduce(
									(s, inv) =>
										s +
										(lines[inv.id].checked
											? parseAmount(lines[inv.id].amount)
											: 0),
									0,
								),
							);
							const sum = round2(acctInvoiceSum + onAccountFor(acct.clientId));
							return (
								<div
									key={acct.clientId}
									className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4"
								>
									{/* Account header + per-account method selector */}
									<div className="flex flex-wrap items-center justify-between gap-3 mb-3 pb-3 border-b border-brand-border">
										<div className="flex items-center gap-3 min-w-0">
											{acctIds.length > 0 && (
												<Tooltip
													content={t('Select all for this account')}
													position="top"
												>
													<input
														type="checkbox"
														aria-label={t('Select all for this account')}
														checked={acctAllChecked}
														ref={(el) => {
															if (el != null) {
																el.indeterminate =
																	acctSomeChecked && !acctAllChecked;
															}
														}}
														onChange={() => {
															toggleAccountSelectAll(acct.openInvoices);
														}}
														className="w-4 h-4 text-portal-accent shrink-0"
													/>
												</Tooltip>
											)}
											<div className="min-w-0">
												<p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
													{t(acct.clientName ?? `#${acct.clientId}`)}
												</p>
												{sum > 0 && (
													<p className="text-xs text-brand-muted">
														{t('Paying')}: {t(formatCurrency(sum) ?? '')}
													</p>
												)}
											</div>
										</div>
										<div>
											<label className="block text-xs font-medium text-brand-muted mb-1">
												{t('Pay with')}
											</label>
											<select
												value={choice}
												onChange={(e) => {
													setAccountChoice((prev) => ({
														...prev,
														[acct.clientId]: e.target.value,
													}));
													clearResults();
												}}
												className="px-3 py-2 rounded-lg bg-brand-input border border-brand-border text-sm max-w-[16rem]"
											>
												<option value={DEFAULT_CHOICE}>
													{t('Default method')}
												</option>
												{acct.savedPaymentMethods.map((m) => (
													<option key={m.id} value={String(m.id)}>
														{t(methodLabel(m))}
													</option>
												))}
												{acct.accountFunds.enabled &&
													acct.accountFunds.available > 0 && (
														<option value={FUNDS_CHOICE}>
															{t('Account Funds')} —{' '}
															{t(
																formatCurrency(acct.accountFunds.available) ??
																	'',
															)}
														</option>
													)}
											</select>
										</div>
									</div>

									{/* Invoices for this account */}
									{acct.openInvoices.length === 0 && (
										<p className="text-sm text-brand-muted">
											{filtersActive
												? t('No invoices match your filters.')
												: t('No open invoices on this account.')}
										</p>
									)}
									<div className="space-y-2">
										{acct.openInvoices.map((invoice) => {
											const line = lines[invoice.id] ?? {
												checked: false,
												amount: '',
											};
											const lineError = lineErrors.get(invoice.id);
											return (
												<div
													key={invoice.id}
													className="flex flex-wrap items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-brand-input"
												>
													<input
														type="checkbox"
														checked={line.checked}
														onChange={() => {
															toggleLine(
																invoice.id,
																invoice.outstandingBalance,
															);
														}}
														className="w-4 h-4 text-portal-accent"
													/>
													<div className="flex-1 min-w-[120px]">
														<p className="text-sm font-medium text-gray-900 dark:text-gray-100">
															{t('Invoice')} #{t(invoice.invoiceNumber)}
														</p>
														<p className="text-xs text-brand-muted">
															{t('Outstanding')}:{' '}
															{t(
																formatCurrency(invoice.outstandingBalance) ??
																	'',
															)}
															{invoice.dueDate != null && (
																<>
																	{' · '}
																	{t('Due')} {t(invoice.dueDate)}
																</>
															)}
														</p>
													</div>
													<div className="relative w-32">
														<span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
															$
														</span>
														<input
															type="number"
															value={line.amount}
															disabled={!line.checked}
															min="0.01"
															step="0.01"
															onChange={(e) => {
																setLineAmount(invoice.id, e.target.value);
															}}
															onBlur={() => {
																setLineAmount(
																	invoice.id,
																	blurCurrency(line.amount),
																);
															}}
															placeholder="0.00"
															className="w-full pl-7 pr-2 py-2 rounded-lg bg-brand-input border border-brand-border text-sm text-right text-gray-900 dark:text-gray-100 disabled:opacity-50"
														/>
													</div>
													{lineError != null && (
														<p className="w-full text-xs text-red-500 text-right">
															{t(lineError)}
														</p>
													)}
												</div>
											);
										})}
									</div>

									{/*
									  On-account deposit for THIS account (PD-14286). Charged to
									  the account's resolved funding method and placed on its
									  balance. Disabled when paying with Account Funds — funds
									  can't be deposited back onto the account.
									*/}
									{accountFundsEnabled && (
										<div className="flex flex-wrap items-center gap-3 p-2 -mx-2 mt-3 border-t border-brand-border pt-3">
											<div className="flex-1 min-w-[120px]">
												<p className="text-sm font-medium text-gray-900 dark:text-gray-100">
													{t('Apply to this account')}
												</p>
												<p className="text-xs text-brand-muted">
													{choice === FUNDS_CHOICE
														? t('Not available when paying with Account Funds.')
														: t(
																'Funds added to this account’s balance instead of an invoice.',
															)}
												</p>
											</div>
											<div className="relative w-32">
												<span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
													$
												</span>
												<input
													type="number"
													value={onAccountByClient[acct.clientId] ?? ''}
													min="0"
													step="0.01"
													disabled={choice === FUNDS_CHOICE}
													onChange={(e) => {
														const value = e.target.value;
														setOnAccountByClient((prev) => ({
															...prev,
															[acct.clientId]: value,
														}));
														clearResults();
													}}
													onBlur={() => {
														setOnAccountByClient((prev) => ({
															...prev,
															[acct.clientId]: blurCurrency(
																prev[acct.clientId] ?? '',
															),
														}));
													}}
													placeholder="0.00"
													className="w-full pl-7 pr-2 py-2 rounded-lg bg-brand-input border border-brand-border text-sm text-right text-gray-900 dark:text-gray-100 disabled:opacity-50"
												/>
											</div>
										</div>
									)}
								</div>
							);
						})
					)}

					{/* Default payment method (used by accounts set to "Default method") */}
					<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
						<div className="flex items-center gap-2 mb-2">
							<FontAwesomeIcon icon={faWallet} className="text-portal-accent" />
							<h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
								{t('Default payment method')}
							</h2>
						</div>
						<p className="text-xs text-brand-muted mb-3">
							{t(
								'Used for any account set to "Default method" above. Stored on your signed-in account.',
							)}
						</p>
						{(context?.defaultSavedPaymentMethods.length ?? 0) > 0 ? (
							<select
								value={defaultMethodId ?? ''}
								onChange={(e) => {
									setDefaultMethodId(parseInt(e.target.value, 10));
									clearResults();
								}}
								className="w-full px-3 py-2 rounded-lg bg-brand-input border border-brand-border text-sm text-gray-900 dark:text-gray-100"
							>
								{context?.defaultSavedPaymentMethods.map((m) => (
									<option key={m.id} value={m.id}>
										{t(methodLabel(m))}
									</option>
								))}
							</select>
						) : (
							<p className="text-xs text-brand-muted">
								{t('No saved payment methods on your account yet.')}
							</p>
						)}
						<button
							type="button"
							onClick={() => {
								setShowAddPayment(true);
							}}
							className="flex items-center gap-1.5 text-xs font-medium text-portal-accent hover:underline mt-2"
						>
							<FontAwesomeIcon icon={faPlus} />
							<span>{t('Add new payment method')}</span>
						</button>
					</div>

					{/* Total + per-account results */}
					<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
						<div className="flex items-center justify-between">
							<span className="text-sm font-medium text-gray-700 dark:text-gray-200">
								{t('Total Payment')}
							</span>
							<span className="text-base font-bold text-gray-900 dark:text-gray-100">
								{t(formatCurrency(total) ?? '')}
							</span>
						</div>
						{results != null && (
							<div className="mt-3 border-t border-brand-border pt-3 space-y-1">
								{results.map((r) => (
									<div
										key={r.clientId}
										className="flex items-center justify-between text-sm"
									>
										<span className="text-gray-900 dark:text-gray-100">
											{t(accountName(r.clientId))}
										</span>
										{r.success ? (
											<span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
												{t(r.transactionStatus ?? 'Paid')}
											</span>
										) : (
											<span className="text-xs text-red-500 text-right ml-3">
												{t(r.error ?? 'Failed')}
											</span>
										)}
									</div>
								))}
							</div>
						)}
					</div>

					{submitError != null && (
						<div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
							<p className="text-sm text-red-600 dark:text-red-400">
								{t(submitError)}
							</p>
						</div>
					)}

					<button
						type="button"
						onClick={() => void handleSubmit()}
						disabled={!canSubmit}
						className="w-full md:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-portal-accent hover:bg-portal-accent-hover disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors min-h-touch mb-6"
					>
						{submitting ? (
							<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
						) : null}
						{submitting
							? t('Processing...')
							: t('Pay {{amount}}', {
									amount: formatCurrency(total > 0 ? total : 0) ?? '',
								})}
					</button>
				</>
			)}

			<Sidebar
				isOpen={showAddPayment}
				onClose={() => {
					setShowAddPayment(false);
				}}
				title={t('Add Payment Method')}
				width="md"
				position="right"
				modal={true}
			>
				<div className="p-4">
					<PortalAddPaymentMethod
						onSuccess={() => {
							setShowAddPayment(false);
							void fetchContext();
						}}
						onCancel={() => {
							setShowAddPayment(false);
						}}
						supportedPaymentMethods={(
							context?.defaultPaymentTypes ?? []
						).filter((pt) => pt.paymentMethodType !== ACCOUNT_FUNDS)}
						preSelectedPaymentType={
							(context?.defaultPaymentTypes ?? []).some(
								(pt) => pt.paymentMethodType === 'ACH',
							)
								? PaymentMethodTypeEnum.ACH
								: PaymentMethodTypeEnum.CREDIT_CARD
						}
					/>
				</div>
			</Sidebar>
		</div>
	);
};

export default PayAccountsView;
